import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { locationPages } from '../../data/seoContent';

const LocationsIndex = () => {
  // Group locations by area
  const capitolHillArea = locationPages.filter(l => 
    ['capitol-hill', 'eastern-market', 'navy-yard', 'union-station'].includes(l.slug)
  );
  const nationalMallArea = locationPages.filter(l => 
    ['national-mall', 'smithsonian', 'washington-monument', 'lincoln-memorial'].includes(l.slug)
  );
  const waterfrontArea = locationPages.filter(l => 
    ['the-wharf', 'navy-yard'].includes(l.slug)
  );
  const northwestDC = locationPages.filter(l => 
    ['georgetown', 'dupont-circle', 'logan-circle', 'adams-morgan', 'national-zoo'].includes(l.slug)
  );

  const LocationCard = ({ location }) => (
    <Link to={`/locations/${location.slug}`}>
      <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
        <div 
          className="h-40 bg-cover bg-center"
          style={{ backgroundImage: `url(${location.heroImage})` }}
        />
        <CardContent className="p-4">
          <h3 className="font-bold text-slate-900 mb-2">{location.title}</h3>
          <p className="text-slate-600 text-sm line-clamp-2 mb-3">{location.intro.slice(0, 120)}...</p>
          <div className="flex items-center text-amber-600 text-sm font-medium">
            View Guide <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  const LocationSection = ({ title, locations, description }) => (
    <div className="mb-16">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">{title}</h2>
      <p className="text-slate-600 mb-6">{description}</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {locations.map(location => (
          <LocationCard key={location.slug} location={location} />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Washington DC Vacation Rental Locations | Capitol View Stays</title>
        <meta name="description" content="Explore Washington DC vacation rental locations. Find the perfect neighborhood for your stay near Capitol Hill, National Mall, Georgetown, and more." />
        <meta name="keywords" content="Washington DC vacation rentals, DC neighborhoods, where to stay in DC" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Washington DC Vacation Rental Locations
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Discover the perfect neighborhood for your DC vacation. Our location guides help you find 
            the ideal area based on attractions, dining, and activities you want to experience.
          </p>
        </div>
      </section>

      {/* Location Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <LocationSection 
            title="Capitol Hill Area"
            description="Stay near the heart of American government with easy access to the Capitol, Library of Congress, and Eastern Market."
            locations={capitolHillArea}
          />

          <LocationSection 
            title="National Mall & Monuments"
            description="Walk to the Smithsonian museums, Washington Monument, and Lincoln Memorial from these central locations."
            locations={nationalMallArea}
          />

          <LocationSection 
            title="Northwest DC Neighborhoods"
            description="Experience DC's most vibrant neighborhoods including Georgetown, Dupont Circle, and Adams Morgan."
            locations={northwestDC}
          />

          {/* All Locations Grid */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">All DC Locations</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {locationPages.map(location => (
                <Link 
                  key={location.slug}
                  to={`/locations/${location.slug}`}
                  className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-amber-50 hover:text-amber-700 transition-colors"
                >
                  <MapPin className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">{location.title.replace('Vacation Rentals Near ', '')}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-amber-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Find Your Perfect DC Vacation Rental
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            No matter which neighborhood you choose, Capitol View Stays offers premium, 
            fully-furnished vacation rentals with everything you need for an unforgettable DC experience.
          </p>
          <Link to="/properties">
            <button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 py-3 rounded-lg transition-colors">
              Browse All Properties
            </button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default LocationsIndex;
